// Умный бот (Этап 66). Переопределяет КЛЮЧЕВЫЕ решения рандомного бота
// осмысленной оценкой, переиспользуя его инфраструктуру (сбор кандидатов,
// построение решения, Моды/Влияние/стелс/активки). Цель — реально сильный
// PvE-соперник; измеряется прогоном против рандомного (tools/_bot-compare.mjs).
//
// Что уже умно:
//   1) Размещение — играем САМУЮ ЦЕННУЮ карту (а не случайную): юниты по
//      статам+ключевым словам, Действия по импакту (снос угрозы / бафф).
//   2) Атака Героем — не лезем в самоубийственный размен; бьём, если убиваем
//      цель или размен выгоден; в лицо — когда оно открыто (win condition).
// Остальное (Моды/Влияние/стелс/Сина/Кироса/мулиган) — пока от рандомного.

import { selectAttackTarget } from '../engine/priorities.js';
import { hasKeyword, KW } from '../engine/keywords.js';
import {
  gatherPlacementCandidates, buildPlacementDecision,
  botPlacement as botPlacementRandom,
  botHeroAttack as botHeroAttackRandom,
  botModPlay as botModPlayRandom,
} from './bot-random.js';
import { actionNeedsTarget } from '../engine/match.js';
import { getActionTargets } from '../engine/match.js';

// ── Сложность по лиге (Этап 68.2, лесенка смягчена в Этапе 73) ────────────
// Бот тупее на низких лигах. Уровень читается из state._botLevel (его
// выставляет точка создания PvE-боя: createMatchForBattle на клиенте /
// startMatch на сервере). Без него — полный smart: тесты, харнесс и
// sandbox остаются максимально сильными.
const LEAGUE_ORDER = [
  'Трущобы', 'Стритраннеры', 'Нетраннеры', 'Фиксеры',
  'Оперативники', 'Корпораты', 'Высшая лига',
];

// Этап 73: шанс умного хода (%) по уровню. Индекс = позиция лиги:
// 0=Трущобы (чистый рандом), 1=Стритраннеры, 2=Нетраннеры, 3=Фиксеры,
// 4=Оперативники и выше (полная сила).
const SMART_CHANCE = [0, 30, 50, 65, 100];
export const FULL_SMART_LEVEL = SMART_CHANCE.length - 1; // 4

export function leagueToBotLevel(leagueName) {
  const name = leagueName === 'Высшая Лига' ? 'Высшая лига' : leagueName;
  const idx = LEAGUE_ORDER.indexOf(name);
  if (idx < 0) return FULL_SMART_LEVEL;    // неизвестно → полный smart (безопасно)
  return Math.min(idx, FULL_SMART_LEVEL);  // Оперативники и выше → полная сила
}

// Делать ли УМНЫЙ ход (иначе — рандомный). Уровень 0..4 из state._botLevel.
function rollSmart(state, rng) {
  const level = state?._botLevel ?? FULL_SMART_LEVEL;
  const chance = SMART_CHANCE[Math.max(0, Math.min(level, FULL_SMART_LEVEL))];
  if (chance >= 100) return true;
  if (chance <= 0) return false;
  return rng.int(100) < chance;
}

// ── Оценка ценности ──────────────────────────────────────────────────────

function kwBonus(keywords, atk) {
  if (!Array.isArray(keywords)) return 0;
  let b = 0;
  if (keywords.includes('frenzy')) b += atk;     // ~двойная атака
  if (keywords.includes('taunt')) b += 2;        // защитная стенка
  if (keywords.includes('pierce')) b += 2;       // игнор брони
  if (keywords.includes('stealth')) b += 2;      // неуязвим до удара
  if (keywords.includes('immune')) b += 3;       // поглощает удар
  return b;
}

// Ценность юнита НА ПОЛЕ (по current-статам).
function unitValueOnField(u) {
  if (!u) return 0;
  const atk = u.current?.atk ?? u.atk ?? 0;
  const hp = u.current?.hp ?? u.hp ?? 0;
  const armor = u.current?.armor ?? u.armor ?? 0;
  let v = atk + hp + armor + kwBonus(u.keywords, atk);
  // Риск Перегрузки (Синтетик): юнит скоро умрёт от Сбоя.
  const mods = u.modifications?.length ?? 0;
  const thr = u.overloadThreshold ?? 3;
  if (mods >= thr) v -= 6;
  else if (mods >= thr - 1) v -= 2;
  // Спящий/не может атаковать — нет немедленного выхлопа.
  if (u.cantAttack) v -= 2;
  return v;
}

// Ценность юнит-карты В РУКЕ (по её статам — current если есть, иначе база).
function unitCardValue(c) {
  const atk = c.current?.atk ?? c.atk ?? c.base?.atk ?? 0;
  const hp = c.current?.hp ?? c.hp ?? c.base?.hp ?? 0;
  const armor = c.current?.armor ?? c.armor ?? c.base?.armor ?? 0;
  return atk + hp + armor + kwBonus(c.keywords, atk);
}

const RARITY_FLAT = { common: 5, rare: 7, epic: 9, legendary: 11, special: 6 };

// Ценность РОЗЫГРЫША кандидата (что сильнее сыграть прямо сейчас).
function cardPlayValue(state, botIdx, cand) {
  const c = cand.card;
  if (cand.kind === 'unit') {
    // +1 — лёгкое предпочтение присутствию на поле перед ситуативными Действиями.
    return unitCardValue(c) + 1;
  }
  // Действие: оценка по направлению цели.
  const enemyIdx = 1 - botIdx;
  let targets = [];
  if (actionNeedsTarget(c.id)) targets = getActionTargets(state, botIdx, c.id) || [];
  const enemyTargets = targets.filter(t => t.ownerId === enemyIdx);
  const allyTargets = targets.filter(t => t.ownerId === botIdx);
  if (enemyTargets.length > 0) {
    // Снос/дебафф — ценность ~ лучшая вражеская цель (убрать угрозу).
    const best = Math.max(...enemyTargets.map(unitValueOnField));
    return 0.7 * best + 2;
  }
  if (allyTargets.length > 0) {
    // Бафф — ценность ~ лучший союзник, но дешевле сноса.
    const best = Math.max(...allyTargets.map(unitValueOnField));
    return 0.4 * best + 2;
  }
  // Без цели — плоско по редкости.
  return RARITY_FLAT[c.rarity] ?? 6;
}

// ── Умное размещение ─────────────────────────────────────────────────────
// Тот же контракт, что botPlacement: возвращает решение или null.
export function botPlacementSmart(state, botIdx, rng) {
  if (!rollSmart(state, rng)) return botPlacementRandom(state, botIdx, rng);
  const candidates = gatherPlacementCandidates(state, botIdx);
  if (candidates.length === 0) return null;
  // Выбираем кандидата с максимальной ценностью розыгрыша (тай-брейк — rng).
  let best = -Infinity;
  for (const cand of candidates) cand._v = cardPlayValue(state, botIdx, cand);
  for (const cand of candidates) if (cand._v > best) best = cand._v;
  const top = candidates.filter(c => c._v >= best - 0.001);
  const pick = top.length === 1 ? top[0] : top[rng.int(top.length)];
  return buildPlacementDecision(state, botIdx, pick, rng);
}

// ── Умная атака Героем ───────────────────────────────────────────────────
// Контракт как botHeroAttack: возвращает Card-цель или null (пропуск).
export function botHeroAttackSmart(state, botIdx, rng) {
  if (!rollSmart(state, rng)) return botHeroAttackRandom(state, botIdx, rng);
  const bot = state.players[botIdx];
  const enemy = state.players[1 - botIdx];
  if (!bot.hero || (bot.hero.current?.hp ?? bot.hero.hp ?? 0) <= 0) return null;
  if (bot.hero.heroAttackedThisTurn && (bot.heroBonusAttackPending || 0) <= 0) return null;

  const sel = selectAttackTarget(bot.hero, enemy, rng);
  if (sel.kind === 'skip') return null;
  // Лицо открыто (нет блокеров) — бьём всегда: это условие победы.
  if (sel.kind === 'hero') return sel.target;

  const heroAtk = bot.hero.current?.atk ?? bot.hero.atk ?? 0;
  const heroHp = bot.hero.current?.hp ?? bot.hero.hp ?? 0;
  const turn = state.currentTurn;
  const hpArmor = (u) => (u.current?.hp ?? 0) + (u.current?.armor ?? 0);
  const counterOf = (u) => (u.cantAttack || (u.isSleeping && u.isSleeping(turn))) ? 0 : (u.current?.atk ?? 0);

  // Свободный выбор (нет провокации): бьём САМУЮ ОПАСНУЮ цель, которую можем
  // убить и пережить контр-удар — вместо «слабейшей» по умолчанию фракции.
  // visible = тот же легальный набор, что у движка (не-стелс, не Спящий).
  if (!sel.redirectedToTaunt) {
    const visible = enemy.unitsOnField().filter(u =>
      u && u.type === 'unit' && !hasKeyword(u, KW.STEALTH) && u.id !== 'deus_inf_spyashy_agent'
    );
    let bestKill = null, bestThreat = -1;
    for (const u of visible) {
      if (heroAtk >= hpArmor(u) && counterOf(u) < heroHp) {   // убьём И выживем
        const threat = u.current?.atk ?? 0;
        if (threat > bestThreat) { bestThreat = threat; bestKill = u; }
      }
    }
    if (bestKill) return bestKill;
    // Выгодного килла нет — берём цель движка и решаем бить/пропустить ниже.
  }

  // Провокация (цель форсится) ИЛИ нет выгодного килла: решаем по размену,
  // чтобы не сливать Героя (это наш win condition).
  const t = sel.target;
  const counter = counterOf(t);
  const kills = heroAtk >= hpArmor(t);
  if (counter >= heroHp && !kills) return null;          // контра убьёт Героя — не бьём
  if (!kills && counter >= heroAtk + 2) return null;     // плохой размен — пропуск
  return t;
}

// ── Умная игра Модами (Синтетик) ─────────────────────────────────────────
// Контракт как botModPlay: {cardInstanceId, targetInstanceId} | null.
// Суть Синтетика — синергия Модов с Героем и юнитами. Выбираем ЛУЧШУЮ пару
// (Мод, цель), а не случайную: ключевые слова под роль (пробитие на бьющего,
// неистовство на выжившего бойца), стак на «карты за Мод» (Биомех/Аддикт/
// Сквилл-лич/Кибер-псих), синергия Героя (Бишор +атк за каждый Мод).

const MOD_PAYOFF = {           // юниты, которым стак Модов особенно выгоден
  syn_biomeh: 3, syn_implant_addikt: 3, syn_skvill_lich: 3, syn_kiberpsih: 2,
  syn_ulichny_naemnik: 1, syn_implantirovanny: 2,
};

function enemyHasArmor(state, botIdx) {
  const enemy = state.players[1 - botIdx];
  for (const u of enemy.unitsOnField()) if ((u.current?.armor ?? 0) >= 2) return true;
  return false;
}

// Ценность навески Мода M на юнита U.
function modAttachScore(state, botIdx, mod, u) {
  const bot = state.players[botIdx];
  const uAtk = u.current?.atk ?? u.atk ?? 0;
  const mAtk = mod.atk ?? mod.base?.atk ?? 0;
  const mHp = mod.hp ?? mod.base?.hp ?? 0;
  const mArmor = mod.armor ?? mod.base?.armor ?? 0;
  let v = mAtk * 1.0 + mHp * 0.7 + mArmor * 0.5;
  const kw = Array.isArray(mod.keywords) ? mod.keywords : [];
  if (kw.includes('pierce')) v += uAtk * 0.5 + (enemyHasArmor(state, botIdx) ? 3 : 0);
  if (kw.includes('frenzy')) v += uAtk;          // удваивает атаку — на бьющего
  if (kw.includes('stealth')) v += 3;
  if (kw.includes('immune')) v += 3;
  v += (MOD_PAYOFF[u.id] || 0);                  // стак на «карты за Мод»
  if (bot.hero?.id === 'syn_bishor') v += 2;     // Бишор: каждый Мод → +атк Герою
  v += uAtk * 0.2;                               // усиливаем своего бьющего
  return v;
}

export function botModPlaySmart(state, botIdx, rng) {
  if (!rollSmart(state, rng)) return botModPlayRandom(state, botIdx, rng);
  const bot = state.players[botIdx];
  if (bot.hero?.faction !== 'syntetics') return null;
  const mods = bot.hand.filter(c => c.type === 'mod');
  if (mods.length === 0) return null;
  // Основная карта должна быть запланирована в этом ходу (иначе Мод сгорит в Ф3).
  const placements = state.pendingPlacements?.[botIdx] || [];
  const pendingMain = placements.some(pl => {
    const c = bot.hand.find(h => h.instanceId === pl.cardInstanceId);
    return c?.type === 'unit' || c?.type === 'action';
  });
  if (!pendingMain && bot.pendingAction == null) return null;
  // Фатальный самоурон (если не Сквилл и не активный Инициатор).
  const isSquill = bot.hero?.id === 'syn_skvill';
  const initiatorActive = !!(bot.activeInitiatorEffect && bot.activeInitiatorEffect.remainingTurns > 0);
  if (!isSquill && !initiatorActive) {
    const nextCost = Math.min((bot.modPlaysCount || 0) + 1, 5);
    if ((bot.hero?.current?.hp ?? 0) <= nextCost) return null;
  }
  // Кандидаты-цели: свои юниты на поле + запланированные в этой Ф2, с местом
  // под Мод (не доводим до Перегрузки: < min(2, threshold-1)).
  const candidates = [];
  for (const u of bot.unitsOnField()) {
    const limit = Math.min(2, (u.overloadThreshold ?? 3) - 1);
    if ((u.modifications?.length ?? 0) < limit) candidates.push(u);
  }
  for (const pl of placements) {
    const c = bot.hand.find(h => h.instanceId === pl.cardInstanceId);
    if (c?.type !== 'unit') continue;
    const limit = Math.min(2, (c.overloadThreshold ?? 3) - 1);
    if ((c.modifications?.length ?? 0) < limit) candidates.push(c);
  }
  if (candidates.length === 0) return null;
  // Лучшая пара (Мод, цель) по ценности навески.
  let best = null, bestV = -Infinity;
  for (const mod of mods) {
    for (const u of candidates) {
      const v = modAttachScore(state, botIdx, mod, u);
      if (v > bestV) { bestV = v; best = { mod, u }; }
    }
  }
  if (!best) return null;
  return { cardInstanceId: best.mod.instanceId, targetInstanceId: best.u.instanceId };
}

// Дескриптор политики для харнесса сравнения. Функции, не указанные тут,
// харнесс берёт у рандомного бота.
export const SMART_POLICY = {
  placement: botPlacementSmart,
  heroAttack: botHeroAttackSmart,
  modPlay: botModPlaySmart,
};

// ── Дроп-ин для игры ─────────────────────────────────────────────────────
// Переключив импорт с bot-random на bot-smart, весь PvE/PvP-фолбэк получает
// УМНЫЕ размещение/Моды/атаку Героем, а остальное (мулиган/Влияние/стелс/
// активки/applyBotPlacementDecision) — без изменений. Тесты и харнесс
// остаются на bot-random. Локальные экспорты ниже перекрывают одноимённые
// из `export *` (правило ESM).
export * from './bot-random.js';
export {
  botPlacementSmart as botPlacement,
  botHeroAttackSmart as botHeroAttack,
  botModPlaySmart as botModPlay,
};
